import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Close, CropFree, Image, Mic, Send } from 'Components/Atoms/Icons';
import useLanguage from 'CustomHooks/useLanguage';
import useRealtimeScribe, {
  joinTranscriptParts,
  loadScribeToken,
  SCRIBE_MODEL_ID,
} from 'CustomHooks/useRealtimeScribe';

type Props = {
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Sends the typed text and any staged attachments as one message. */
  onSubmit: (text: string, files: File[]) => void | Promise<void>;
  /** Captures a screenshot and returns it as a File to stage. */
  onScreenshot?: () => Promise<File | null>;
};

const MAX_INPUT_HEIGHT = 140;

const AttachmentPreview = ({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) => {
  const [url, setUrl] = useState('');
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  useEffect(() => {
    if (!isImage && !isVideo) return;
    // Build the preview URL from a fresh Blob (not the raw File) so the
    // rendered src is a same-origin blob: URL with no path back to DOM input.
    const objectUrl = URL.createObjectURL(new Blob([file], { type: file.type }));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, isImage, isVideo]);

  return (
    <div className="support-attach-chip">
      {isImage && url ? (
        <img className="support-attach-chip__thumb" src={url} alt={file.name} />
      ) : isVideo && url ? (
        <video className="support-attach-chip__thumb" src={url} muted />
      ) : (
        <span className="support-attach-chip__name">📎 {file.name}</span>
      )}
      <button
        type="button"
        className="support-attach-chip__remove"
        aria-label="Anhang entfernen"
        onClick={onRemove}
      >
        <Close />
      </button>
    </div>
  );
};

const SupportComposer = ({
  placeholder,
  disabled,
  autoFocus,
  onSubmit,
  onScreenshot,
}: Props) => {
  const { language } = useLanguage();
  const [value, setValue] = useState('');
  const [attachments, setAttachments] = useState<{ id: string; file: File }[]>(
    [],
  );
  const [capturing, setCapturing] = useState(false);
  const [voiceError, setVoiceError] = useState(false);
  const [sendError, setSendError] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const baseValueRef = useRef('');
  const committedRef = useRef('');
  const attachmentIdRef = useRef(0);

  // autoFocus only acts on mount, and the overview keeps the composer mounted.
  // A layout effect, so the focus still falls inside the tap that asked for it.
  useLayoutEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const grow = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, []);

  const apply = useCallback(
    (partial = '') => {
      setValue(
        joinTranscriptParts(baseValueRef.current, committedRef.current, partial),
      );
      requestAnimationFrame(grow);
    },
    [grow],
  );

  const {
    status,
    isConnected,
    isTranscribing,
    partialTranscript,
    connect,
    disconnect,
    clearTranscripts,
  } = useRealtimeScribe({
    modelId: SCRIBE_MODEL_ID,
    languageCode: language === 'en' ? 'en' : 'de',
    onPartialTranscript: ({ text }) => apply(text),
    onCommittedTranscript: ({ text }) => {
      committedRef.current = joinTranscriptParts(committedRef.current, text);
      apply();
    },
    onError: () => setVoiceError(true),
  });

  const listening = status === 'connecting' || isConnected || isTranscribing;

  const toggleMic = async () => {
    if (listening) {
      disconnect();
      return;
    }
    setVoiceError(false);
    try {
      baseValueRef.current = value;
      committedRef.current = '';
      clearTranscripts();
      const token = await loadScribeToken();
      await connect({ token });
    } catch {
      // token-load / early-connect failures never reach the hook status
      setVoiceError(true);
    }
  };

  const addFiles = (files: File[]) =>
    setAttachments((prev) => [
      ...prev,
      ...files.map((file) => ({ id: `att-${attachmentIdRef.current++}`, file })),
    ]);

  const takeScreenshot = async () => {
    if (!onScreenshot || capturing || disabled) return;
    setCapturing(true);
    try {
      const file = await onScreenshot();
      if (file) addFiles([file]);
    } finally {
      setCapturing(false);
    }
  };

  const resetInput = () => {
    setValue('');
    baseValueRef.current = '';
    committedRef.current = '';
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const submit = async () => {
    const trimmed = value.trim();
    if ((!trimmed && !attachments.length) || disabled) return;
    if (listening) disconnect();
    const files = attachments.map((a) => a.file);
    try {
      setSendError(false);
      await onSubmit(trimmed, files);
      resetInput();
      setAttachments([]);
    } catch {
      // keep the text + attachments so the user can retry
      setSendError(true);
    }
  };

  const canSend = !!value.trim() || !!attachments.length;

  return (
    <div
      className={`support-composer${listening ? ' support-composer--listening' : ''}`}
    >
      {!!attachments.length && (
        <div className="support-composer__attachments">
          {attachments.map((attachment) => (
            <AttachmentPreview
              key={attachment.id}
              file={attachment.file}
              onRemove={() =>
                setAttachments((prev) =>
                  prev.filter((a) => a.id !== attachment.id),
                )
              }
            />
          ))}
        </div>
      )}

      <textarea
        ref={inputRef}
        className="support-composer__input"
        autoFocus={autoFocus}
        placeholder={listening ? 'Sprich jetzt …' : placeholder}
        value={value}
        disabled={disabled}
        rows={1}
        onChange={(e) => {
          setValue(e.target.value);
          grow();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />

      <div className="support-composer__bar">
        <div className="support-composer__tools">
          {onScreenshot && (
            <button
              type="button"
              className="support-composer__tool"
              aria-label="Screenshot anhängen"
              disabled={disabled || capturing}
              onClick={takeScreenshot}
            >
              <CropFree />
            </button>
          )}
          <button
            type="button"
            className={`support-composer__tool${
              listening ? ' support-composer__tool--active' : ''
            }`}
            aria-label={listening ? 'Spracheingabe stoppen' : 'Spracheingabe'}
            disabled={disabled && !listening}
            onClick={toggleMic}
          >
            <Mic />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) addFiles(files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="support-composer__tool"
            aria-label="Bild oder Video hochladen"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <Image />
          </button>
        </div>

        <button
          type="button"
          className="support-composer__send"
          aria-label="Senden"
          disabled={disabled || !canSend}
          onClick={submit}
        >
          <Send />
        </button>
      </div>

      {!!partialTranscript && (
        <span className="support-composer__hint">{partialTranscript}</span>
      )}

      {sendError && (
        <span className="support-error">
          Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.
        </span>
      )}

      {voiceError && (
        <span className="support-error">
          Spracheingabe nicht möglich. Bitte erneut versuchen.
        </span>
      )}
    </div>
  );
};

export default SupportComposer;
